import { create } from 'zustand';
import { IUser } from '../@shared/interfaces';
import { userService } from '../services/api/user.service';

interface AuthStore {
  // Token management
  getToken: (() => Promise<string>) | null;
  getTokenSilently: ((options?: any) => Promise<string>) | null;
  setGetToken: (fn: () => Promise<string>) => void;
  setGetTokenSilently: (fn: (options?: any) => Promise<string>) => void;

  // Auth0 logout function
  logout: ((options?: any) => void) | null;
  setLogout: (fn: (options?: any) => void) => void;

  // User and profile data
  user: IUser | null;
  profile: IUser | null;
  isLoadingProfile: boolean;
  profileError: Error | null;
  isInitialized: boolean;

  // Logout confirmation state
  showLogoutConfirmation: boolean;
  setShowLogoutConfirmation: (show: boolean) => void;

  // Auth actions
  setUser: (user: IUser | null) => void;
  clearUser: () => void;
  clearAuthAndCache: (options: {
    redirect?: boolean;
    logout?: (options?: any) => void;
    queryClient?: any;
    redirectPage?: string;
  }) => void;

  // Profile actions
  fetchProfile: () => Promise<IUser | null>;
  updateProfile: (data: Partial<IUser>) => Promise<IUser | null>;
  setIsInitialized: (isInitialized: boolean) => void;
}

export const useAuthStore = create<AuthStore>((set) => ({
  // Initial token state
  getToken: null,
  getTokenSilently: null,
  setGetToken: (fn) => set({ getToken: fn }),
  setGetTokenSilently: (fn) => set({ getTokenSilently: fn }),

  // Auth0 logout function
  logout: null,
  setLogout: (fn) => set({ logout: fn }),

  // Initial user and profile state
  user: null,
  profile: null,
  isLoadingProfile: false,
  profileError: null,
  isInitialized: false,

  // Logout confirmation state
  showLogoutConfirmation: false,
  setShowLogoutConfirmation: (show) => set({ showLogoutConfirmation: show }),

  // User management
  setUser: (user) => set({ user }),
  clearUser: () =>
    set({
      user: null,
      getToken: null,
      getTokenSilently: null,
      logout: null,
      profile: null,
      profileError: null,
    }),

  clearAuthAndCache: ({ logout, queryClient, redirect, redirectPage }) => {
    useAuthStore.getState().clearUser();
    if (queryClient) {
      queryClient.clear();
    }
    if (logout && redirect && redirectPage) {
      logout({
        logoutParams: { returnTo: window.location.origin + redirectPage },
      });
    } else if (redirect && redirectPage) {
      window.location.href = redirectPage;
    }
  },

  setIsInitialized: (isInitialized) => set({ isInitialized }),

  // Profile management
  fetchProfile: async () => {
    try {
      set((state) => ({
        isLoadingProfile: true,
        // Keep any existing profile data during loading
        profile: state.profile,
        profileError: null,
      }));

      const response = await userService.getProfile();
      if (response.success && response.user) {
        // Update BOTH profile AND user to ensure all components see the changes
        set({
          profile: response.user,
          user: response.user, // Sync user with profile
          isLoadingProfile: false,
        });
        return response.user;
      } else {
        // Don't clear the profile on non-auth errors
        set((state) => ({
          profile: state.profile, // Keep any existing profile data
          profileError: new Error(
            response.message || 'Failed to fetch profile'
          ),
          isLoadingProfile: false,
        }));
        return null;
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error occurred';

      // Don't clear the profile on network errors
      set((state) => ({
        profile: state.profile, // Keep any existing profile data
        profileError: new Error(errorMessage),
        isLoadingProfile: false,
      }));
      console.error('Error fetching user profile:', error);
      return null;
    }
  },

  updateProfile: async (data: Partial<IUser>) => {
    try {
      set((state) => ({
        isLoadingProfile: true,
        // Keep existing profile during updates
        profile: state.profile,
        profileError: null,
      }));

      const response = await userService.updateProfile(data);
      if (response.success && response.user) {
        // Update BOTH profile AND user to ensure all components see the changes
        set({
          profile: response.user,
          user: response.user, // Sync user with profile
          isLoadingProfile: false,
        });
        return response.user;
      } else {
        // Don't clear profile on update error
        set((state) => ({
          profile: state.profile,
          profileError: new Error(
            response.message || 'Failed to update profile'
          ),
          isLoadingProfile: false,
        }));
        return null;
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error occurred';

      // Keep existing profile on network errors
      set((state) => ({
        profile: state.profile,
        profileError: new Error(errorMessage),
        isLoadingProfile: false,
      }));
      console.error('Error updating user profile:', error);
      return null;
    }
  },
}));
